/**
 * ATL-1123 — the P&L's opening / closing product-stock lines, and an honest account of what was
 * missing when they could not be computed.
 *
 * The two lines come from `productStockSnapshots/<d-M-y-storeId>-stock`: the document for the FIRST
 * day of the reported range is the opening stock, the document for the LAST day is the closing one.
 * Between 2026-06-04 and this fix nothing wrote those documents at all (the writer's call site was
 * deleted — see `utils/validationFunctions.js#saveProductStockSnapshot`), and the screen turned
 * every absent document into a plausible `0 ₸`.
 *
 * So the contract here is: a missing snapshot is NOT a zero. `computeStockAmountsForRange` returns
 * `null` for an amount it could not read every input for, together with the exact list of documents
 * it wanted, and the screen renders that as unavailable rather than as money.
 *
 * ATL-1279 narrowed "it": the refusal is decided per BOUNDARY, not for the pair. A range whose
 * opening day falls inside the outage above and whose closing day does not has a closing valuation
 * that is complete, over a document the live writer wrote — withholding it too reported nothing
 * where the books hold something. The difference still needs both, and the rule that made the
 * refusal worth having is untouched: an amount is never a partial sum across stores.
 */

/**
 * The marker a snapshot writes for a product it has no stock figure for (see
 * `STOCK_SNAPSHOT_UNKNOWN_CELL`). It is the string 'NaN', which is TRUTHY — the screen's old
 * `(snapshot[id] || 0) * price` therefore multiplied a string and produced NaN, poisoning the whole
 * total and every store total summed after it.
 *
 * Only a NUMBER, or a STRING that parses to a finite one, is a known figure — the same rule
 * `reconstruct-stock-snapshots-atl1123.ts` applies to an anchor cell. `Number()` coerces far more
 * than that: `Number(true)` is 1, `Number([])` is 0, `Number([7])` is 7 and `Number(new Date())` is
 * an epoch in milliseconds, so a bare `Number(raw)` reports every one of them as a KNOWN quantity
 * and prices it. That is reachable, not hypothetical: the pre-ATL-1123 writer wrote
 * `storeStock[store]` through verbatim (it only tested `=== undefined`), so a snapshot written
 * before this ticket can hold whatever the product document held — and inventing a stock of 1 unit
 * out of a boolean is exactly the fabricated quantity this module exists to refuse.
 *
 * @param {unknown} raw
 * @returns {{known: boolean, quantity: number}}
 */
export const readStockCell = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? { known: true, quantity: raw } : { known: false, quantity: 0 }
  if (typeof raw !== 'string' || raw.trim() === '') return { known: false, quantity: 0 }
  const quantity = Number(raw)
  if (!Number.isFinite(quantity)) return { known: false, quantity: 0 }
  return { known: true, quantity }
}

/**
 * Whether a snapshot document actually carries stock cells. `getProductStockSnapshot` returns
 * `{...snap.data(), id, ref}` for a MISSING document too — i.e. `{id, ref}` with `data()` undefined
 * spread to nothing — so "did the read find anything?" cannot be asked of truthiness. A snapshot
 * that exists but holds only its own `date` is equally unusable.
 *
 * @param {Object|null|undefined} snapshot
 */
export const snapshotHasStockCells = (snapshot) => {
  if (!snapshot) return false
  for (const key of Object.keys(snapshot)) {
    if (key === 'id' || key === 'ref' || key === 'date' || key === 'store') continue
    return true
  }
  return false
}

/**
 * Whether the snapshot carries a usable stock figure for ANY product in the catalogue — active in
 * this store or not.
 *
 * This is the falsifier for the degenerate case `stockPurchaseAmount` cannot otherwise report: a
 * store for which NO product is marked `activeInStore` considers nothing, so it returns
 * `{amount: 0, knownProductCount: 0, unknownProductIds: []}` — indistinguishable, to the caller,
 * from a store that genuinely carries no goods. `activeInStore` is optional in Firestore and the
 * rest of the app reads an ABSENT entry as active (`screens/Products/BatchProductEditScreen.js`
 * uses `activeInStore?.[storeId] ?? true`), so an org whose products predate per-store activation
 * has real stock and an empty active set. Valuing that at 0 ₸ and calling it complete is the same
 * fabricated zero this module exists to remove. If the document holds real figures while the
 * valuation considered none of them, say so instead.
 *
 * @param {Object|null|undefined} snapshot
 * @param {Object} productsDict
 */
export const snapshotHasKnownCellForAnyProduct = (snapshot, productsDict) => {
  for (const product of Object.values(productsDict || {})) {
    if (product?.isComposite) continue
    // `product?.id`, not `product.id`: `stockPurchaseAmount` above skips a null/undefined entry
    // through its `!product?.activeInStore?.[storeId]` guard, so this walk must tolerate the same
    // dictionary. Reading `.id` off it would throw INSIDE the P&L's load and take the whole
    // report down over a degenerate catalogue entry.
    if (readStockCell(snapshot?.[product?.id]).known) return true
  }
  return false
}

/**
 * Purchase-price valuation of one store's stock as of one snapshot.
 *
 * Only products marked active in that store and not composite are valued — composites are assembled
 * from their ingredients, so counting both would double-count the same goods.
 *
 * @returns {{amount: number, unknownProductIds: string[], knownProductCount: number}}
 *   `unknownProductIds` are active products the snapshot has no usable figure for. They are valued
 *   at 0 and RETURNED to the caller — `computeStockAmountsForRange` totals them as
 *   `unknownCellCount`, which `ProfitStats` RENDERS. It has to: valuing an unknown at 0 understates
 *   the total by exactly the stock nobody knows about, and a reconstructed snapshot
 *   (`scripts/reconstruct-stock-snapshots-atl1123.ts`) writes the unknown marker for every cell
 *   whose ledger does not close — for a 38%-reconstructable org that is most of the value, and it
 *   would otherwise leave the screen showing a confident number for a third of the business.
 *   `knownProductCount` is how many active products the snapshot
 *   DID have a figure for — zero of them means the valuation below is not a small total, it is no
 *   information at all, and the caller must not report it as money.
 */
export const stockPurchaseAmount = (snapshot, productsDict, storeId) => {
  let amount = 0
  let knownProductCount = 0
  const unknownProductIds = []
  for (const product of Object.values(productsDict || {})) {
    if (!product?.activeInStore?.[storeId] || product?.isComposite) continue
    const cell = readStockCell(snapshot?.[product.id])
    if (cell.known) knownProductCount += 1
    else unknownProductIds.push(product.id)
    amount += cell.quantity * (Number(product?.purchasePrice) || 0)
  }
  return { amount, unknownProductIds, knownProductCount }
}

/**
 * "This line has no figure." THE predicate — exported, because the banner below, the rendered line
 * and the exported cell in `ProfitStats` are all claims about the same thing, and two spellings of
 * it in two files is how they come to disagree. `computeStockAmountsForRange` only ever answers
 * `null`, but a caller reading a field off a shape that never got one hands `undefined` here, and
 * `=== null` would then read it as a REPORTED amount — naming the wrong boundary in the banner
 * while the line above it says "no data".
 *
 * @param {number|null|undefined} amount
 */
export const stockAmountUnavailable = (amount) => amount === null || amount === undefined

/**
 * Which sentence the P&L's missing-snapshot banner should use, or null when there is nothing to
 * say. Lives here, beside the rule it describes, so the two cannot drift: the banner is a claim
 * about which amounts were withheld, and `computeStockAmountsForRange` is what withholds them.
 *
 * ATL-1279: before per-boundary completeness there was one sentence — "product cost not
 * calculated" — and it was true, because a single missing document withheld both lines. It is
 * false the moment one of them is reported, and it would have been false on the screen that
 * produced this ticket (a readable closing stock, an opening one three months inside the ATL-1123
 * writer outage). An entry with no `boundary` is not a day/store pair at all ('store-key-mismatch',
 * 'store-not-in-dict', 'no-catalogue', …), so it keeps the sentence that names no count.
 *
 * @param {{startProductAmount: number|null, endProductAmount: number|null, missing: Array}} amounts
 * @returns {string|null} an i18n key.
 */
export const missingStockMessageKey = ({ startProductAmount, endProductAmount, missing }) => {
  if (!missing || missing.length === 0) return null
  if (missing.some(entry => !entry?.boundary)) return 'stats.stockSnapshotsUnreadable'
  const openingAbsent = stockAmountUnavailable(startProductAmount)
  const closingAbsent = stockAmountUnavailable(endProductAmount)
  if (openingAbsent && closingAbsent) return 'stats.stockSnapshotsMissing'
  // Exactly one of the two is absent here: every `missing` entry withholds at least one boundary,
  // so both being reported is unreachable.
  return openingAbsent ? 'stats.stockSnapshotsMissingOpening' : 'stats.stockSnapshotsMissingClosing'
}

/**
 * Opening and closing stock valuation for a P&L range.
 *
 * @param {Object}   params
 * @param {Array}    params.statDocs      daily-stat docs for the range, ASCENDING by date. Their
 *                                        ids are the snapshot ids minus the `-stock` suffix.
 * @param {string}   params.store         a store id, or 'all'.
 * @param {Object}   params.storesDict    keyed by store id (the same key space as the stat docs'
 *                                        `store` field and as the snapshot document ids).
 * @param {Object}   params.productsDict
 * @param {Function} params.loadSnapshot  `(snapshotId) => Promise<Object>`.
 * @returns {Promise<{startProductAmount: number|null, endProductAmount: number|null,
 *   productAmountDifference: number|null, complete: boolean, reason: string|null, missing: Array,
 *   unknownCellCount: number, asymmetricUnknownCount: number, differenceComparable: boolean|null,
 *   storesCovered: number}>} Each amount is `null` unless EVERY store the range covers contributed
 *   to it; `productAmountDifference` needs both. `complete` stays "every document this range wanted
 *   was read, for BOTH boundaries" — which is stricter than `missing` being empty, because an empty
 *   range wants none and is still not complete — and `unknownCellCount` counts only the cells
 *   inside an amount that is actually reported. `asymmetricUnknownCount` is how many of those cells
 *   are unvalued at one boundary and valued at the other (ATL-1281); `differenceComparable` is
 *   `false` when there is at least one, and `null` — never `true` — when no difference was
 *   reported. `reason` is set only when no store could be
 *   walked at all: 'no-catalogue' (no products loaded — nothing can be valued), 'no-stores' (the
 *   store list has not loaded, so the 'all' fan-out has nothing to intersect), 'no-stat-docs'
 *   (the range is empty — nothing is missing) or 'store-key-mismatch' (the stat docs are keyed in a
 *   different id space than `storesDict`).
 */
export const computeStockAmountsForRange = async ({
  statDocs,
  store,
  storesDict,
  productsDict,
  loadSnapshot,
}) => {
  const missing = []
  /**
   * ATL-1279 — completeness is decided per BOUNDARY, not for the pair.
   *
   * `refuse()` raises the flag of every boundary the failure actually spoils, so an unwritten
   * opening snapshot no longer withholds a closing valuation that was read from a document that
   * exists. What it must NOT weaken: a boundary is still all-or-nothing ACROSS stores (one store's
   * unreadable closing withholds the closing TOTAL, never a partial sum of the rest), and a
   * failure that is not about one boundary — an unreachable store, a key mismatch, a catalogue
   * that never loaded — still withholds both.
   */
  const blocked = { opening: false, closing: false }
  /**
   * Unknown cells, kept per boundary READ rather than in one running total, so only the ones
   * inside an amount the screen actually shows are reported. A one-day range reads ONE document
   * for both boundaries, so its entry carries both roles and is counted once however many of them
   * are reported — counting it per role would restate one hollow document as two.
   */
  const unknownReads = []
  let startTotal = 0
  let endTotal = 0
  let storesCovered = 0

  /**
   * Record a refusal against the boundaries it spoils. `roles` omitted means BOTH, deliberately:
   * a reason added later without thinking about boundaries then withholds more, not less.
   */
  const refuse = (entry, roles) => {
    missing.push(entry)
    for (const role of roles ?? ['opening', 'closing']) blocked[role] = true
  }

  // The catalogue is the only thing that turns a snapshot's cells into money. With none loaded
  // every `stockPurchaseAmount` below iterates an empty dictionary and returns
  // `{amount: 0, knownProductCount: 0, unknownProductIds: []}` — which passes BOTH degenerate-case
  // clauses further down (`snapshotHasKnownCellForAnyProduct` also walks `productsDict`, so it
  // cannot see the figures either) and reports a confident 0 ₸ as complete. `productsDict` is
  // populated asynchronously, so "the catalogue has not arrived yet" is indistinguishable from
  // "this business owns nothing" — and the writer refuses the symmetric case ('no-products' in
  // `saveProductStockSnapshot`) rather than freezing an empty valuation into the books. Refuse here
  // for the same reason.
  if (Object.keys(productsDict || {}).length === 0) {
    return {
      startProductAmount: null,
      endProductAmount: null,
      productAmountDifference: null,
      complete: false,
      reason: 'no-catalogue',
      missing: [{ storeId: null, boundary: null, snapshotId: null, reason: 'no-catalogue' }],
      unknownCellCount: 0,
      asymmetricUnknownCount: 0,
      // `null`, not `true`, for the same reason the main return says so: no difference was
      // reported, so there is nothing to call comparable. An ABSENT field here would be worse
      // still — `stockAmountUnavailable` exists because a caller reading a field off a shape that
      // never got one is how "unknown" comes to read as its opposite.
      differenceComparable: null,
      storesCovered: 0,
    }
  }

  const docsInRange = Array.isArray(statDocs) ? statDocs : []
  const knownStoreIds = Object.keys(storesDict || {})
  const statStoreIds = [...new Set(docsInRange.map(doc => doc?.store).filter(Boolean))]
  /** Can a stat doc's `store` be turned into a snapshot document id we can actually read? */
  const reachable = (storeId) => (store === 'all' ? knownStoreIds.includes(storeId) : storeId === store)
  // Stat docs whose store is not in `storesDict`'s key space — a store deleted since it traded.
  // Until ATL-1118 this was EVERY doc in Supabase read mode, because `transformDailyStat` answered
  // with the Supabase store UUID. Their boundary days are unreadable, and dropping them silently is
  // how a PARTIAL sum comes to be presented as the whole business — the exact failure this module
  // exists to stop.
  const unreachableStoreIds = statStoreIds.filter(storeId => !reachable(storeId))

  // 'all' fans out per store because a snapshot document is per store; a single store is just the
  // one-element case of the same walk, so both paths below share this function and cannot drift.
  const storeIds =
    store === 'all'
      ? knownStoreIds.filter(storeId => statStoreIds.includes(storeId))
      : [store]

  for (const storeId of storeIds) {
    // A stat doc with no `store` at all cannot be attributed to one of several stores, so the
    // 'all' fan-out keeps its strict match; the single-store path accepts it, because there the
    // caller already filtered the query by store.
    const docs =
      store === 'all'
        ? docsInRange.filter(doc => doc?.store === storeId)
        : docsInRange.filter(doc => !doc?.store || doc.store === storeId)
    if (docs.length === 0) continue
    storesCovered += 1

    const openingStatId = docs[0]?.id ?? null
    const closingStatId = docs[docs.length - 1]?.id ?? null
    // A one-day range has the SAME document for both boundaries. Reading it once is not only a
    // saved round trip: counting it twice would report one absent document as two missing
    // "day/store combinations" in the banner, which is the plausible-but-false number all over
    // again — this time in the very message that exists to admit the data is missing. The same
    // applies when BOTH ids are null (an id-less boundary doc): that is one unreadable boundary,
    // not two, so the equality below deliberately collapses the null === null case as well.
    const boundaries =
      openingStatId === closingStatId
        ? [{ boundary: 'opening+closing', roles: ['opening', 'closing'], statId: openingStatId }]
        : [
            { boundary: 'opening', roles: ['opening'], statId: openingStatId },
            { boundary: 'closing', roles: ['closing'], statId: closingStatId },
          ]
    for (const { boundary, roles, statId } of boundaries) {
      if (!statId) {
        refuse({ storeId, boundary, snapshotId: null, reason: 'no-stat-id' }, roles)
        continue
      }
      const snapshotId = statId + '-stock'
      let snapshot = null
      try {
        snapshot = await loadSnapshot(snapshotId)
      } catch (error) {
        refuse({ storeId, boundary, snapshotId, reason: 'read-failed', error: error?.message }, roles)
        continue
      }
      if (!snapshotHasStockCells(snapshot)) {
        refuse({ storeId, boundary, snapshotId, reason: 'not-written' }, roles)
        continue
      }
      const { amount, unknownProductIds, knownProductCount } = stockPurchaseAmount(snapshot, productsDict, storeId)
      // A snapshot that exists but holds a usable figure for NONE of the store's active products
      // values them all at 0 and reports the sum as fact. That is the same fabricated zero as an
      // absent document, wearing a document's clothes — e.g. a snapshot written before the store's
      // entire current catalogue existed. Report it as missing rather than as 0 ₸.
      if (knownProductCount === 0 && unknownProductIds.length > 0) {
        refuse({ storeId, boundary, snapshotId, reason: 'all-cells-unknown' }, roles)
        continue
      }
      // The same "no information at all" with the OTHER shape: nothing was even considered,
      // because no product is marked active in this store. `unknownProductIds` is then empty too,
      // so the clause above cannot see it — and the total is a confident 0 ₸ over an empty loop.
      // Only report it when the document demonstrably HOLDS figures we declined to look at; a
      // store that genuinely carries none of the catalogue is a real 0.
      if (
        knownProductCount === 0 &&
        unknownProductIds.length === 0 &&
        snapshotHasKnownCellForAnyProduct(snapshot, productsDict)
      ) {
        refuse({ storeId, boundary, snapshotId, reason: 'no-active-products' }, roles)
        continue
      }
      // The IDS, not just the count: ATL-1281 needs to know whether the SAME product was unvalued
      // at both ends (which leaves the difference wrong only by what that product moved) or at
      // only one (which moves it by that product's whole stock). A count cannot answer that.
      unknownReads.push({ storeId, roles, ids: unknownProductIds })
      if (roles.includes('opening')) startTotal += amount
      if (roles.includes('closing')) endTotal += amount
    }
  }

  // `storesCovered === 0` has two very different causes and neither may be reported as "N snapshot
  // documents were not written" — with an empty `missing` that sentence renders the count 0, which
  // is the same shape of plausible-but-false number this ticket exists to remove:
  //
  //  - the range genuinely holds no daily stats. Nothing is missing; there is nothing to value.
  //  - the stat docs' `store` key is not in `storesDict`'s key space. That was ATL-1118 in Supabase
  //    read mode: `transformDailyStat` answered with the Supabase store UUID while `storesDict` and
  //    the snapshot document ids are keyed by FIRESTORE store id, so the fan-out matched nothing.
  //    It is a real, reportable failure — record it rather than letting it read like an empty
  //    range. It stays reachable after that fix: a store deleted since it traded, and any till
  //    still talking to a backend that predates it.
  let reason = null
  if (storesCovered === 0) {
    if (docsInRange.length === 0) {
      reason = 'no-stat-docs'
    } else if (store === 'all' && knownStoreIds.length === 0) {
      // The 'all' fan-out intersects the stat docs' stores with `storesDict`, so an EMPTY store
      // list matches nothing for a reason that has nothing to do with the key space. `storesDict`
      // is populated asynchronously exactly like `productsDict`, so name it the way the catalogue
      // case above is named — reporting a store list that has not arrived as 'store-key-mismatch'
      // points the reader at ATL-1118 (a read-mode defect) for what is a load that has not landed.
      reason = 'no-stores'
      refuse({ storeId: null, boundary: null, snapshotId: null, reason })
    } else {
      reason = 'store-key-mismatch'
      refuse({ storeId: null, boundary: null, snapshotId: null, reason })
    }
  } else {
    // Some stores were walked and others could not be reached at all. Reporting only the stores we
    // could read would be the partial total again, so each unreachable store is recorded — that
    // makes `complete` false and the screen says so instead of showing a number for part of the
    // business.
    for (const storeId of unreachableStoreIds) {
      refuse({ storeId, boundary: null, snapshotId: null, reason: 'store-not-in-dict' })
    }
    // A stat doc carrying NO `store` at all cannot be attributed to one of several stores, so the
    // 'all' fan-out above skips it. Skipping it silently is the partial total again — the day it
    // covers is simply absent from both boundaries and from `missing` — so record it here.
    if (store === 'all' && docsInRange.some(doc => !doc?.store)) {
      refuse({ storeId: null, boundary: null, snapshotId: null, reason: 'stat-doc-without-store' })
    }
  }

  // A partial sum across stores is the failure this ticket exists to stop: it looks exactly like a
  // real total. So an amount is reported only when EVERY store the range covers contributed to it
  // — `blocked` carries that, because a per-store refusal raises the whole boundary's flag.
  const openingReported = storesCovered > 0 && !blocked.opening
  const closingReported = storesCovered > 0 && !blocked.closing
  // `complete` keeps its old meaning exactly — "every document this range wanted was read, for both
  // boundaries" — and must not drift into the weaker "something was reported". It is NOT the same
  // predicate as `missing.length === 0`: an empty range wants no document at all, so it has nothing
  // missing and is still not complete (`storesCovered === 0`, reason 'no-stat-docs'). Nothing on
  // the P&L reads it today — the banner keys off `missing` and the amounts — so it exists for
  // callers that want "was the whole pair readable?" without re-deriving it.
  const complete = openingReported && closingReported
  // The difference spans both boundaries, so it needs exactly what `complete` asserts.
  const difference = complete ? endTotal - startTotal : null
  // Keyed by the same role names `boundaries` uses, so the lookup below cannot silently answer for
  // the WRONG boundary the way a two-branch ternary on `role` would if a third role were ever added.
  const reported = { opening: openingReported, closing: closingReported }

  /**
   * ATL-1281 — how many products were valued at only ONE of the two boundaries.
   *
   * An unvalued cell is priced at zero, so a product unvalued at the opening and valued at the
   * closing moves the difference by its whole stock with nothing having been bought or sold. That
   * is what a reconstructed opening beside a live closing does: measured on `uV2Urt7u6YuXDRu2TBge`,
   * 140 products refused on 2 Sep and 7 on 3 Sep, and the books read 4,986,651 → 6,715,452
   * overnight.
   *
   * Only the SYMMETRIC DIFFERENCE of the two id sets counts, and it is a FLOOR on the error, not
   * the whole of it. A product unvalued at BOTH ends contributes zero to both totals, so the delta
   * is wrong only by whatever that product actually moved over the period — bounded by its
   * trading, where a one-sided cell moves the delta by the entire stock level. `unknownCellCount`
   * already admits the both-ends case, and repeating it here would attach the alarming sentence to
   * the milder failure. So `differenceComparable === true` means "neither boundary was valued over
   * a population the other did not see" — NOT "the difference is exact".
   *
   * Per STORE, because a product is stocked per store: the same id unvalued in store A's opening
   * and in store B's closing is two separate one-sided products, not a matched pair.
   *
   * No ₸ figure accompanies it, deliberately. The reason a cell is unknown is that nobody knows the
   * quantity; the value it would contribute is therefore unknowable, and any amount printed beside
   * this count would be the fabricated number this module exists to refuse. The count is knowable.
   */
  const unknownIdsFor = (role) => {
    const perStore = new Map()
    for (const read of unknownReads) {
      if (!read.roles.includes(role)) continue
      const set = perStore.get(read.storeId) ?? new Set()
      for (const id of read.ids) set.add(id)
      perStore.set(read.storeId, set)
    }
    return perStore
  }
  const openingUnknown = unknownIdsFor('opening')
  const closingUnknown = unknownIdsFor('closing')
  let asymmetricUnknownCount = 0
  for (const storeId of new Set([...openingUnknown.keys(), ...closingUnknown.keys()])) {
    const open = openingUnknown.get(storeId) ?? new Set()
    const close = closingUnknown.get(storeId) ?? new Set()
    for (const id of open) if (!close.has(id)) asymmetricUnknownCount += 1
    for (const id of close) if (!open.has(id)) asymmetricUnknownCount += 1
  }

  return {
    startProductAmount: openingReported ? startTotal : null,
    endProductAmount: closingReported ? endTotal : null,
    productAmountDifference: difference,
    complete,
    reason,
    missing,
    unknownCellCount: unknownReads.reduce(
      (total, read) => total + (read.roles.some(role => reported[role]) ? read.ids.length : 0),
      0,
    ),
    asymmetricUnknownCount,
    // `null`, not `true`, when no difference was reported: there is no number to qualify, and a
    // `true` here would read as "the difference is fine" about a difference nobody was given.
    differenceComparable: difference === null ? null : asymmetricUnknownCount === 0,
    storesCovered,
  }
}
